import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { UserProfile, UserRole } from '../api/models/userModels';


/**
 * Authentication state interface
 */
interface AuthState {
  user: UserProfile | null;
  token: string | null;
  role: UserRole | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Login credentials interface
 */
interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Login response interface
 */
interface LoginResponse {
  user: UserProfile;
  token: string;
}

/**
 * Initial authentication state
 */
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('auth_token'),
  role: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

/**
 * Async thunk for user login
 */
export const loginUser = createAsyncThunk<
  LoginResponse,
  LoginCredentials,
  { rejectValue: string }
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message || 'Login failed');
      }

      const data: LoginResponse = await response.json();
      
      // Store token in localStorage
      localStorage.setItem('auth_token', data.token);
      
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Network error occurred'
      );
    }
  }
);

/**
 * Async thunk for checking authentication status
 */
export const checkAuth = createAsyncThunk<
  UserProfile,
  void,
  { rejectValue: string }
>(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        return rejectWithValue('No token found');
      }

      const response = await fetch('/api/v1/auth/me', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        localStorage.removeItem('auth_token');
        return rejectWithValue('Authentication failed');
      }

      const user: UserProfile = await response.json();
      return user;
    } catch (error) {
      localStorage.removeItem('auth_token');
      return rejectWithValue(
        error instanceof Error ? error.message : 'Network error occurred'
      );
    }
  }
);

/**
 * Async thunk for user logout
 */
export const logoutUser = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (token) {
        await fetch('/api/v1/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
      
      // Clear token regardless of API response
      localStorage.removeItem('auth_token');
    } catch (error) {
      // Clear token even if logout request fails
      localStorage.removeItem('auth_token');
      return rejectWithValue(
        error instanceof Error ? error.message : 'Logout error occurred'
      );
    }
  }
);

/**
 * Authentication slice
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Update user information
     */
    updateUser: (state, action: PayloadAction<Partial<UserProfile>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      } else {
        // If user doesn't exist yet, create it from the payload
        state.user = action.payload as UserProfile;
      }
      state.isAuthenticated = true; // Add this line
      state.role = action.payload.role as UserRole || state.role; // Update role if provided
    },
    
    /**
     * Update user role
     */
    updateRole: (state, action: PayloadAction<UserRole>) => {
      state.role = action.payload;
      if (state.user) {
        state.user.role = action.payload;
      }
    },
    
    /**
     * Clear authentication error
     */
    clearError: (state) => {
      state.error = null;
    },
    
    /**
     * Set authentication token
     */
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem('auth_token', action.payload);
      state.isAuthenticated = true;
    },
    
    /**
     * Clear authentication state (manual logout)
     */
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.role = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem('auth_token');
    },
  },
  extraReducers: (builder) => {
    // Login user
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.role = action.payload.user.role;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Login failed';
        state.isAuthenticated = false;
      });

    // Check authentication
    builder
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.role = action.payload.role;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.role = null;
        state.isAuthenticated = false;
        state.error = action.payload || 'Authentication check failed';
      });

    // Logout user
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.role = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state) => {
        state.isLoading = false;
        // Clear state even on error
        state.user = null;
        state.token = null;
        state.role = null;
        state.isAuthenticated = false;
      });
  },
});

/**
 * Action creators
 */
export const { updateUser, updateRole, clearError, setToken, clearAuth } = authSlice.actions;

/**
 * Selectors
 */
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectToken = (state: RootState) => state.auth.token;
export const selectRole = (state: RootState) => state.auth.role;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.isLoading;
export const selectAuthError = (state: RootState) => state.auth.error;

/**
 * Role checking selectors
 */
export const selectIsAthlete = (state: RootState) => 
  state.auth.role === 'athlete' || state.auth.role === 'both';

export const selectIsTrainer = (state: RootState) => 
  state.auth.role === 'trainer' || state.auth.role === 'both';

export const selectIsSuperuser = (state: RootState) => 
  state.auth.role === 'superuser';

export const selectHasRole = (role: UserRole) => (state: RootState) =>
  state.auth.role === role || state.auth.role === 'both' || state.auth.role === 'superuser';

/**
 * Export reducer as default
 */
export default authSlice.reducer;